import { Feature, Map } from 'ol';
import {
  Draw, Modify, Translate,
} from 'ol/interaction';
import { Pixel } from 'ol/pixel';
import { Group } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import {
  Circle, LineString, Point, Polygon,
} from 'ol/geom';
import { Extent, getCenter } from 'ol/extent';
import {
  Fill, Style, Circle as CircleS, Stroke, Text,
} from 'ol/style';
import { Coordinate } from 'ol/coordinate';
import { noModifierKeys } from 'ol/events/condition';
import { Dispatch, SetStateAction } from 'react';
import { transform } from 'ol/proj';
import ImageLayer from 'ol/layer/Image';
import { ImageStatic } from 'ol/source';
import LayerName from './enums';
import { getLayerByName } from './utils';
import colors from '../../../configs/colors';
import { initialCenter } from '../../../configs/initialValues';
import zIndexes from '../../../configs/zIndexes';

class LineStringInteractionUtils {
  map: Map;

  constructor(map: Map) {
    this.map = map;
  }

  cleanInteractions() {
    // I dont know why but we have to do this twice
    [...Array(2)].forEach(() => this.map.getInteractions().forEach((interaction) => {
      if (
        interaction instanceof Modify
                || interaction instanceof Draw
                || interaction instanceof Translate) {
        this.map.removeInteraction(interaction);
      }
    }));
  }

  cleanInteraction(name: string) {
    this.map.getInteractions().forEach((interaction) => {
      if (interaction.get('name') === name) {
        this.map.removeInteraction(interaction);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getPixelDistance(a: Pixel, b: Pixel) {
    return Math.sqrt(((a[0] - b[0]) ** 2) + ((a[1] - b[1]) ** 2));
  }

  isInteraction(name: string) {
    return this.map.getInteractions().getArray().find((i) => i.get('name') === name);
  }

  generateTrackingLayer() {
    const geoLayers = getLayerByName(this.map, LayerName.GeoLayer) as Group;
    const firstGeoLayer = geoLayers.getLayers().item(0) as ImageLayer<ImageStatic>;
    const firstGeoLayerExtent = firstGeoLayer.getSource()?.getImageExtent();
    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
    });
    const geometry = new Circle(getCenter(firstGeoLayerExtent as Extent), 1);
    const feature = new Feature(geometry);

    source.addFeature(feature);
    layer.set('name', LayerName.TrackingLayer);
    layer.setZIndex(zIndexes.CROSS_SECTION + 1);

    return layer;
  }

  generateShadowLayer() {
    const extent = this.map.getView().calculateExtent(this.map.getSize()) as Extent;
    const minX = extent[0];
    const minY = extent[1];
    const maxX = extent[2];
    const maxY = extent[3];

    const polygon = new Polygon([
      [
        [minX, minY],
        [minX, maxY],
        [maxX, maxY],
        [maxX, minY],
        [minX, minY],
      ],
    ]);

    const feature = new Feature(polygon);

    feature.setStyle(new Style({
      fill: new Fill({
        color: 'rgba(255, 100, 100, 0)',
      }),
    }));

    const shadowLayer = new VectorLayer({
      source: new VectorSource({
        features: [feature],
      }),
      className: 'lightbox',
    });

    shadowLayer.set('name', LayerName.ShadowLayer);
    shadowLayer.setZIndex(zIndexes.CROSS_SECTION - 1);

    return shadowLayer;
  }

  generateVectorLayer() {
    const calculateTranslatedCoords = (coordinate: Coordinate, distance: number = 10) => {
      const pixelCoordinates = this.map.getPixelFromCoordinate(coordinate) || transform(initialCenter, 'EPSG:4326', this.map?.getView().getProjection());
      pixelCoordinates[0] += distance;
      pixelCoordinates[1] -= distance;
      return this.map.getCoordinateFromPixel(pixelCoordinates) || transform(initialCenter, 'EPSG:4326', this.map?.getView().getProjection());
    };

    const generatePoint = (coordinate: Coordinate, name: string) => {
      const styles = [];

      styles.push(
        new Style({
          geometry: new Point(coordinate),
          image: new CircleS({
            fill: new Fill({
              color: 'white',
            }),
            stroke: new Stroke({
              color: colors.primary,
              width: 5,
            }),
            radius: 8,
          }),
        }),
      );

      styles.push(
        new Style({
          geometry: new Point(calculateTranslatedCoords(coordinate)),
          text: new Text({
            text: name,
            fill: new Fill({ color: '#FFFFFF' }),
            font: '12px Roboto',
            offsetY: -10,
          }),
        }),
      );
      return styles;
    };

    const vectorLayer = new VectorLayer({
      source: new VectorSource({ wrapX: false }),
      style: (feature) => {
        const geometry = feature.getGeometry() as LineString;
        const styles = [
          new Style({
            stroke: new Stroke({
              color: colors.primary,
              width: 4,
            }),
          }),
        ];

        geometry?.forEachSegment((start, end) => {
          styles.push(...generatePoint(start, 'A'));
          styles.push(...generatePoint(end, 'B'));
        });
        return styles;
      },
    });

    vectorLayer.set('name', LayerName.CrossSectionLayer);
    vectorLayer.setZIndex(zIndexes.CROSS_SECTION);

    return vectorLayer;
  }

  // eslint-disable-next-line class-methods-use-this
  generateModifyInteraction(
    source: VectorSource,
    setIsModifying: (opt: boolean)=> void,
    setSegment: Dispatch<SetStateAction<Feature<LineString> | undefined>>,
  ) {
    const modifyInteraction = new Modify({
      source,
      pixelTolerance: 5,
    });
    modifyInteraction.set('name', 'modifyInteraction');

    modifyInteraction.on('modifystart', () => {
      setIsModifying(true);
    });

    modifyInteraction.on('modifyend', (event) => {
      const lineFeature = event.features.item(0) as Feature<LineString>;

      const feature = new Feature<LineString>();
      feature.setGeometry(lineFeature.getGeometry());

      if (feature) {
        setSegment(feature);
        setIsModifying(false);
      }
    });

    return modifyInteraction;
  }

  // eslint-disable-next-line class-methods-use-this
  generateTranslateInteraction(
    vectorLayer: VectorLayer<VectorSource>,
    setIsTranslating: (opt: boolean) => void,
    setSegment: Dispatch<SetStateAction<Feature<LineString> | undefined>>,
  ) {
    const translateInteraction = new Translate({
      layers: [vectorLayer],
      condition: noModifierKeys,
    });

    translateInteraction.on('translatestart', () => {
      setIsTranslating(true);
    });

    translateInteraction.on('translateend', (event) => {
      setIsTranslating(false);
      const lineFeature = event.features.item(0) as Feature<LineString>;

      const feature = new Feature<LineString>();
      feature.setGeometry(lineFeature.getGeometry());

      if (feature) { setSegment(feature); }
    });

    translateInteraction.set('name', 'translateInteraction');

    return translateInteraction;
  }

  // eslint-disable-next-line class-methods-use-this
  generateDrawInteraction(
    source: VectorSource,
    setShowCrossSectionModal: (opt: boolean) => void,
    setSegment: Dispatch<SetStateAction<Feature<LineString> | undefined>>,
    setLine: (line: Draw) => void,
  ) {
    const draw = new Draw({
      source,
      type: 'LineString',
      maxPoints: 2,
    });
    draw.on('drawstart', () => {
      source.clear();
    });

    draw.on('drawend', (event) => {
      const lineFeature = event.feature as Feature<LineString>;
      setShowCrossSectionModal(true);
      setSegment(lineFeature);
    });

    setLine(draw);
    draw.set('name', LayerName.CrossSectionLayer);

    return draw;
  }
}

export default LineStringInteractionUtils;
